import { useMemo } from 'react'

import { ActionIcon, Badge, Menu, Tooltip } from '@mantine/core'

import { useAuth } from 'src/auth'
import { isAdminOrOps } from 'src/lib/auth/permissions'
import CopyableId from 'src/components/DataDisplay/CopyableId/CopyableId'
import DnTable from 'src/components/Core/DnTable'
import { DocumentUserStatus } from 'src/graphql/types/documents'
import { RoleType } from 'src/graphql/types/employees'
import IconInfoCircle from 'src/icons/IconInfoCircle'
import IconThreeDots from 'src/icons/IconThreeDots'
import { formatSnakeValue } from 'src/lib/formatters'

interface DocumentTemplate {
  documentTemplateId: string
  documentName: string
  signingToolRefId: string
  documentCategory: string
  signers: string[]
  userStatus: string
  status: string
}

interface DocumentTemplatesTableProps {
  templates: DocumentTemplate[]
  loading: boolean
  onRowClick: (templateId: string) => void
  onDeleteClick?: (template: DocumentTemplate) => void
  totalCount?: number
  onLoadPage?: (pageInfo: { offset: number; limit: number }) => void
  defaultResultsPerPage?: number
  onPageChange?: (page: number) => void
  paginationProps?: {
    initialPage?: number
    onResultsPerPageChange?: (resultsPerPage: number) => void
  }
}

const DocumentTemplatesTable = ({
  templates,
  loading,
  onRowClick,
  onDeleteClick,
  totalCount = templates.length,
  onLoadPage,
  defaultResultsPerPage = 20,
  onPageChange,
  paginationProps,
}: DocumentTemplatesTableProps) => {
  const { currentUser } = useAuth()
  const isAdmin = isAdminOrOps(currentUser)

  const columns = useMemo(
    () => [
      {
        key: 'documentTemplateId',
        label: 'ID',
        width: 5,
        render: (template: DocumentTemplate) => (
          <CopyableId value={template.documentTemplateId} label="Template ID" />
        ),
      },
      ...(isAdmin
        ? [
            {
              key: 'signingToolRefId',
              label: 'Anvil ID',
              width: 5,
              render: (template: DocumentTemplate) => (
                <CopyableId value={template.signingToolRefId} label="Anvil ID" />
              ),
            },
          ]
        : []),
      {
        key: 'documentName',
        label: 'Document Name',
        width: 30,
        render: (template: DocumentTemplate) => (
          <Tooltip
            label={template.documentName}
            disabled={template.documentName?.length <= 35}
            withArrow
          >
            <span className="block max-w-full truncate text-xs text-doubleNickel-gray-900">
              {template.documentName}
            </span>
          </Tooltip>
        ),
      },
      {
        key: 'documentCategory',
        label: 'Category',
        width: 15,
        render: (template: DocumentTemplate) => (
          <span className="text-xs text-doubleNickel-gray-600">
            {formatSnakeValue(template.documentCategory)}
          </span>
        ),
      },
      {
        key: 'signers',
        label: 'Signers',
        width: 20,
        render: (template: DocumentTemplate) => (
          <div className="flex flex-wrap gap-1">
            {template.signers?.map((signer) => (
              <Badge
                key={signer}
                size="xs"
                variant="light"
                color={signer === 'APPLICANT' ? 'blue' : 'green'}
              >
                {signer === 'APPLICANT' ? 'Applicant' : 'Employee'}
              </Badge>
            ))}
          </div>
        ),
      },
      {
        key: 'userStatus',
        label: (
          <div className="flex items-center gap-1">
            <span>Review Status</span>
            <Tooltip
              label="The status the document is automatically set to once all signatures are completed."
              withArrow
              multiline
              maw={200}
            >
              <span className="flex items-center">
                <IconInfoCircle className="h-3 w-3 fill-none stroke-doubleNickel-gray-400" />
              </span>
            </Tooltip>
          </div>
        ),
        width: 15,
        render: (template: DocumentTemplate) => (
          <Badge
            size="xs"
            variant="light"
            color={template.userStatus === DocumentUserStatus.COMPLETED ? 'green' : 'yellow'}
          >
            {formatSnakeValue(template.userStatus)}
          </Badge>
        ),
      },
      {
        key: 'actions',
        label: '',
        width: 10,
        render: (template: DocumentTemplate) => (
          <div className="flex justify-end">
            <Menu shadow="md" position="bottom-end">
              <Menu.Target>
                <ActionIcon
                  variant="subtle"
                  size={20}
                  onClick={(e) => e.stopPropagation()}
                >
                  <IconThreeDots className="h-4 w-4 fill-doubleNickel-white stroke-doubleNickel-gray-600" />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown className="w-32">
                {onDeleteClick && (
                  <Menu.Item
                    className="text-xs text-doubleNickel-error-600"
                    onClick={(e) => {
                      e.stopPropagation()
                      onDeleteClick(template)
                    }}
                  >
                    Delete
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          </div>
        ),
      },
    ],
    [onDeleteClick, isAdmin]
  )

  return (
    <DnTable
      columns={columns}
      data={templates}
      loading={loading}
      enableColumnResize={false}
      enablePagination={true}
      totalCount={totalCount}
      onLoadPage={onLoadPage}
      defaultResultsPerPage={defaultResultsPerPage}
      onPageChange={onPageChange}
      paginationProps={paginationProps}
      onRowClick={(_, row) => {
        const template = row as DocumentTemplate
        if (template?.documentTemplateId) {
          onRowClick(template.documentTemplateId)
        }
      }}
      emptyStateMessage="No document templates found"
      rowClassName="cursor-pointer hover:bg-doubleNickel-gray-50"
    />
  )
}

export default DocumentTemplatesTable
